//
// # Layout "Manager"
// Mostly the thing that knows how to apply changes to the layout data model.
// As of this writing, use cases are WIP, so this is not yet used.
//

// Local Imports
import { Result, exhaust, Uuid } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { IdMap } from "../id";
import * as graphics from "../graphics";
import { Ident, IdentMap } from "../ident";
import { LayoutChange, Stack } from "./layout";

class Instances extends IdMap<schema.layout.Instance> {}
class Pins extends IdMap<schema.layout.LayoutPin> {}
class Routes extends IdMap<schema.layout.Route> {}
class Blockages extends IdMap<schema.layout.Blockage> {}

export class LayoutManager {
  private constructor(readonly ident: Ident) {}

  // Create a new LayoutManager from a v4 Layout
  // Turn all its instances, pins, routes, and blockages into our in-memory classes
  static new(content: schema.Layout) {
    const me = new LayoutManager(content.ident);
    // In v4, there's just a single layout, no hierarchy
    me.layouts_idmap.set(content);

    content.impl?.instances.forEach((inst: schema.layout.Instance) =>
      me.instances.add(inst)
    );
    me.bbox = content.abs.bbox;
    me.outline = content.abs.outline;
    content.abs.pins.forEach((pin: schema.layout.LayoutPin) =>
      me.pins.add(pin)
    );
    content.impl?.routes.forEach((route: schema.layout.Route) =>
      me.routes.add(route)
    );
    content.abs.blockages.forEach((blockage: schema.layout.Blockage) =>
      me.blockages.add(blockage)
    );

    // Stack is referenced by ID in v4, not embedded
    // If needed, would be loaded from depot
    return me;
  }
  private bbox = graphics.Bbox.empty();
  private outline: schema.Bbox | undefined;
  private layouts_idmap = new IdentMap<schema.Layout>();
  private stack: schema.pdk.Stack = Stack.empty(Uuid.new() as schema.StackId); // Initialize with empty stack
  readonly instances = new Instances();
  readonly pins = new Pins();
  readonly routes = new Routes();
  readonly blockages = new Blockages();
  readonly changeManager = new ChangeManager(this);

  // Add a layout definition to the manager (for when tools create new layouts)
  addLayout = (layout: schema.Layout): void => {
    this.layouts_idmap.set(layout);
  };

  readonly export = (): schema.Layout => {
    // Export as a single v4 Layout
    const layout: schema.Layout = {
      schemaVersion: schema.SCHEMA_VERSION,
      contentKind: "layout",
      ident: this.ident,
      impl: {
        instances: Array.from(this.instances.values()),
        routes: Array.from(this.routes.values()),
        conns: [],
        elems: [],
      },
      abs: {
        bbox: this.bbox,
        outline: this.outline,
        pins: Array.from(this.pins.values()),
        blockages: Array.from(this.blockages.values()),
      },
      stackId: this.stack.id,
    };

    // Update the layout in the idmap
    this.layouts_idmap.set(layout);

    return layout;
  };

  applyChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<unknown>> => {
    return this.changeManager.applyChangeRequest(changeList);
  };
}

export class ChangeManager {
  constructor(private mgr: LayoutManager) {}

  //
  // # Convert a change request
  //
  // Main entry point for most of this class's action.
  // Any array of (anythings) comes in, we validate they are valid changes,
  // look up all the things they refer to, and if successful,
  // return a list of in-memory changes class instances.
  // Essentially everything else in this class is in service of this method.
  //
  readonly applyChangeRequest = async (
    changeList: Array<unknown>
  ): Promise<Result<null>> => {
    //
    // Handle empty change list as a valid case
    if (!changeList || changeList.length === 0) {
      return Result.Ok(null);
    }

    // Do two steps of conversion: validation, then to our in-memory model
    const { validate } = LayoutChange.validator;
    const validations = changeList.map(validate);
    const promises = validations.map((r) => r.andThenAsync(this.applyChange));

    // And this next bit is basically `Result.all` - but we want to keep track of the errors
    const resultsList = await Promise.all(promises);
    const oks = resultsList.filter((r) => r.ok).map((r) => r.val);
    const errs = resultsList.filter((r) => r.err).map((r) => r.val);

    if (errs && errs.length > 0) {
      return Result.Err({ why: "error-applying-changes", errs });
    }

    const [first, ...rest] = oks;
    return Result.Ok(null);
  };

  // Convert a (serializable) `Change` into an in-memory one
  private applyChange = async (change: LayoutChange): Promise<Result<null>> => {
    //
    switch (change.changeKind) {
      case "add":
        return this.applyAddChange(change);
      case "update":
        return this.applyUpdateChange(change);
      case "remove":
        return this.applyRemoveChange(change);
      case "unsupported":
        return Result.Err({
          why: "unsupported",
          message: "Unsupported Change Request",
        });
      case "set-property":
        // Property changes are handled at the state manager level, not in LayoutManager
        return Result.Err({
          why: "not-implemented",
          message:
            "set-property changes are handled at the state manager level",
        });
      case "remove-property":
        // Property changes are handled at the state manager level, not in LayoutManager
        return Result.Err({
          why: "not-implemented",
          message:
            "remove-property changes are handled at the state manager level",
        });
      case "set-outline":
        return Result.Err({
          why: "not-implemented",
          message: "set-outline changes are handled at the state manager level",
        });
      case "remove-outline":
        return Result.Err({
          why: "not-implemented",
          message:
            "remove-outline changes are handled at the state manager level",
        });
      default:
        exhaust(change);
    }
  };

  // Convert a `remove` to an in-memory `Change`.
  // Mostly searches for the entity to remove, and returns an error if it can't find it.
  private applyRemoveChange = (
    change: LayoutChange & { changeKind: "remove" }
  ): Result<null> => {
    const { id } = change;
    const { targetKind } = change.data;
    const find = () => {
      switch (targetKind) {
        case "blockage":
          return false; // FIXME! real blockages
        case "instance":
          return this.mgr.instances.remove(id);
        case "route":
          return this.mgr.routes.remove(id);
        case "pin":
          return this.mgr.pins.remove(id);
        default:
          exhaust(targetKind);
      }
    };
    return find()
      ? Result.Ok(null)
      : Result.Err({
          why: "invalid-change-entity-not-found",
          message: "Remove non-existent entity",
        });
  };

  private applyUpdateChange = async (
    change: LayoutChange & { changeKind: "update" }
  ): Promise<Result<null>> => {
    const { id, data } = change;
    const { targetKind } = data;
    if (id === undefined) {
      return Result.Err({
        why: "invalid-change-entity-no-id",
        message: "Update entity with no ID",
      });
    }
    const find = () => {
      switch (targetKind) {
        case "instance":
          return this.mgr.instances.update({ ...data, id });
        case "pin":
          return this.mgr.pins.update({ ...data, id });
        case "route":
          return this.mgr.routes.update({ ...data, id });
        case "blockage":
          return this.mgr.blockages.update({ ...data, id });
        default:
          return false;
      }
    };
    return find()
      ? Result.Ok(null)
      : Result.Err({
          why: "id-not-found-or-unknown-targetkind",
          message: `Failed to update ${targetKind} with id ${id}`,
        });
  };

  private applyAddChange = async (
    change: LayoutChange & { changeKind: "add" }
  ): Promise<Result<null>> => {
    const { targetKind } = change.data;
    const find = () => {
      switch (targetKind) {
        case "instance":
          return this.mgr.instances.add(change.data);
        case "pin":
          return this.mgr.pins.add(change.data);
        case "route":
          return this.mgr.routes.add(change.data);
        case "blockage":
          return this.mgr.blockages.add(change.data);
        default:
          // exhaust(targetKind);
          return Result.Err({
            why: "not-implemented",
            message: "NotImplemented: applyAddChange for non Instance",
          });
      }
    };
    return find()
      ? Result.Ok(null)
      : Result.Err({
          why: "not-implemented",
          message: "NotImplemented: add a blockage",
        });
  };
}
